import React, { useState } from "react";
import {
  Edit, Trash2, Filter, FileText, Calendar, Globe, Link,
  CheckCircle
} from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/badge";

interface Category {
  id: number;
  name: string;
  status: string;
  source: string;
  formCount: number;
  lastSync: string;
  region: string;
}

interface CategoriesTabProps {
  syncedCategories: Category[];
  autoSync: boolean;
  searchQuery: string;
  onToggleAutoSync: () => void;
}

const CategoriesTab: React.FC<CategoriesTabProps> = ({
  syncedCategories,
  autoSync,
  searchQuery,
  onToggleAutoSync
}) => {
  const CategoryCard = ({ category }: { category: Category }) => (
    <Card className="p-4 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium text-sm sm:text-base">{category.name}</h3>
            <Badge variant={undefined} className={`text-xs sm:text-sm ${category.status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {category.status.charAt(0).toUpperCase() + category.status.slice(1)}
            </Badge>
            <Badge variant="outline" className={`text-xs sm:text-sm ${category.source === "auto" ? "border-blue-200 text-blue-700" : "border-purple-200 text-purple-700"}`}>
              {category.source === "auto" ? "Auto-synced" : "Manual"}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4" /> {category.formCount} forms
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4" /> Last sync: {category.lastSync}
            </span>
            <span className="flex items-center gap-1">
              <Globe className="h-3 w-3 sm:h-4 sm:w-4" /> Region: {category.region}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm">
            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600">
            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Categories</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage service categories and auto-sync settings</p>
        </div>
        <Button className="w-full sm:w-auto flex items-center gap-2 text-sm">
          <Filter className="h-3 w-3 sm:h-4 sm:w-4" /> Add Category
        </Button>
      </div>

      <Card className="p-4 sm:p-4 bg-blue-50 border-blue-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            <span className="font-medium text-sm sm:text-base">Auto-sync Categories</span>
          </div>
          <Button
            variant={autoSync ? "default" : "outline"}
            onClick={onToggleAutoSync}
            className="w-full sm:w-auto flex items-center gap-2 text-sm"
          >
            {autoSync ? (
              <>
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" /> Enabled
              </>
            ) : (
              "Enable"
            )}
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:gap-4">
        {syncedCategories
          .filter(category => category.name.toLowerCase().includes(searchQuery.toLowerCase()))
          .map(category => <CategoryCard key={category.id} category={category} />)}
      </div>
    </div>
  );
};

export default CategoriesTab;
